import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from 'dayjs';
import { InboundTransactionTypeCode, LiquidityScenarioTransaction, OutboundTransactionTypeCode, TransactionOrigin, TransactionStatus, TransactionType, TransactionTypeCode } from "@/types/liquidity-api";
import { MenuItem, Select, FormControl, InputLabel, InputAdornment } from "@mui/material";

interface EditTransactionDialogProps {
  isOpen: boolean;
  closeFunction: () => void;
  saveTransactionFunction: (LiquidityScenarioTransaction) => void;
  transaction?: LiquidityScenarioTransaction
  isCreateNewTransaction: boolean;
}

export const EditTransactionDialog = ({
  isOpen,
  closeFunction,
  saveTransactionFunction,
  transaction,
  isCreateNewTransaction
}: EditTransactionDialogProps) => {
  const isEditableTransaction = transaction?.origin === TransactionOrigin.Manual || isCreateNewTransaction;
  const hasEditedTransactionDate = !!transaction?.modifiedTransactionDate;
  const [name, setName] = useState(transaction?.otherPart || "");
  const [amount, setAmount] = useState(transaction?.modifiedAmount ? transaction?.modifiedAmount : transaction?.amount || '');
  const [date, setDate] = useState<Dayjs | null>(dayjs(hasEditedTransactionDate ? transaction?.modifiedTransactionDate : transaction?.transactionDate));
  const [transactionType, setTransactionType] = useState(transaction?.transactionType || TransactionType.Invoice);
  const [transactionTypeCode, setTransactionTypeCode] = useState(transaction?.transactionTypeCode || "");
  const [status, setStatus] = useState(transaction?.status || TransactionStatus.Pending);

  const updateTransactionType = (transactionType: string | TransactionType) => {
    // When we change the transaction type, we need to clear the transaction type code
    setTransactionType(Number(transactionType));
    setTransactionTypeCode("");
  }

  const isAmountEdited = amount !== transaction?.modifiedAmount;
  const isDateEdited = date !== dayjs(hasEditedTransactionDate ? transaction?.modifiedTransactionDate : transaction?.transactionDate);
  const isEdited = isAmountEdited || isDateEdited;

  // Only show the reset button if the we are editing an existing transaction
  const shouldShowResetButton = !isCreateNewTransaction;

  const allFieldsExists = !!name.trim() && !!amount && !!date && !!transactionType && !!status && !!transactionTypeCode;
  const isSaveEnabled = allFieldsExists;
  const isOriginalAmount = amount === transaction?.amount;
  const isOriginalDate = date && transaction?.transactionDate ?
    date.toDate().toDateString() === new Date(transaction.transactionDate).toDateString() :
    false;


  const handleSave = () => {
    const amountToSave = amount === '' ? 0 : transactionType === TransactionType.Invoice ? Math.abs(Number(amount)) : -Math.abs(Number(amount));
    // Save transaction
    if (isCreateNewTransaction) {
      // Transaction is not available, so we're creating a new Transaction.
      const newTransaction: LiquidityScenarioTransaction = {
        key: crypto.randomUUID(),
        origin: TransactionOrigin.Manual,
        amount: amountToSave,
        transactionDate: date?.toDate(),
        otherPart: name,
        status: status,
        transactionType: transactionType,
        invoiceNumber: "n/a",
        externalInvoiceNumber: "n/a",
      }
      saveTransactionFunction(newTransaction);
    } else {
      // Update existing transaction
      if (isEditableTransaction) {
        // Allow updating all fields
        const updatedTransaction: LiquidityScenarioTransaction = {
          ...transaction,
          amount: amountToSave,
          transactionDate: date?.toDate(),
          otherPart: name,
          status: status,
          transactionType: transactionType,
          transactionTypeCode: transactionTypeCode,
          modifiedAmount: isOriginalAmount ? null : amountToSave, // Clean field if the amount is the same as the original
          modifiedTransactionDate: isOriginalDate ? null : date?.toDate(), // Clean field if the date is the same as the original
        }
        saveTransactionFunction(updatedTransaction);
      } else {
        // Only allow updating amount and date
        const updatedTransaction: LiquidityScenarioTransaction = {
          ...transaction,
          modifiedAmount: isOriginalAmount ? null : amountToSave,
          modifiedTransactionDate: isOriginalDate ? null : date?.toDate(),
          status: status,
        }
        saveTransactionFunction(updatedTransaction);
      }

    }

    closeFunction();
  };

  const handleReset = () => {
    // Reset transaction
    setAmount(transaction?.amount);
    setDate(dayjs(transaction?.transactionDate));
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => closeFunction()}
      title="Edit Transaction"
      footer={
        <div className="flex flex-row-reverse justify-between items-center w-full pr-4 pl-4 pb-2">
          <div className="flex space-x-2">
            <Button variant="outlined" onClick={() => closeFunction()}>
              Cancel
            </Button>
            <Button onClick={handleSave} color="success" disabled={!isSaveEnabled}>
              Save
            </Button>
          </div>
          {
            shouldShowResetButton && (<Button color="error" onClick={handleReset} disabled={!isEdited}>
              Reset
            </Button>)
          }
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-6 py-4">
        <Input
          className="col-span-2"
          label="Name"
          placeholder="Description"
          value={name}
          disabled={!isEditableTransaction}
          onChange={(e) => setName(e.target.value)}
        />

        <Input
          label="Amount"
          type="number"
          size="medium"
          value={Math.abs(Number(amount)).toString()}
          onChange={(e) =>
            // Only allow positive numbers, and depending on the transaction type convert it to a negative number on save
            setAmount(Math.abs(Number(e.target.value)))
          }
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">{transactionType === TransactionType.SupplierInvoice ? '-' : ''}</InputAdornment>,
            },
          }}

        />

        <DatePicker
          label="Date"
          value={date}
          onChange={(newValue) => setDate(newValue)}
        />

        <FormControl>
          <InputLabel>Transaction Type</InputLabel>
          <Select
            label="Transaction Type"
            value={transactionType}
            onChange={(e) => updateTransactionType(e.target.value as TransactionType)}
            disabled={!isEditableTransaction}
          >
            <MenuItem value={1}>Inbetalning</MenuItem>
            <MenuItem value={2}>Utbetalning</MenuItem>
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Transaction Type Code</InputLabel>
          <Select
            label="Transaction Type Code"
            value={transactionTypeCode}
            onChange={(e) => setTransactionTypeCode(e.target.value)}
            disabled={!isEditableTransaction}
          >
            {(transactionType === 1 ? Object.values(InboundTransactionTypeCode) : transactionType === 2 ? Object.values(OutboundTransactionTypeCode) : []).map((code) => (
              <MenuItem key={code.value} value={code.value}>
                {code.label}
              </MenuItem>
            ))}

          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as TransactionStatus)}
          >
            <MenuItem value="1">Inkluderad</MenuItem>
            <MenuItem value="2">Exkluderad</MenuItem>
          </Select>
        </FormControl>
      </div>
    </Dialog >
  );
};